/**
 * OAuth Connect Screen
 *
 * Full-screen gate shown when no OAuth token exists for the organization.
 * Blocks all app content until the user completes the OAuth flow.
 *
 * Two variants:
 * - "connect" — first-time connection (no token ever stored)
 * - "reconnect" — token expired or revoked
 */

'use client';

import { useState } from 'react';
import { useMondayAuth } from '@/contexts/MondayAuthContext';

interface ConnectScreenProps {
  variant: 'connect' | 'reconnect';
}

export function ConnectScreen({ variant }: ConnectScreenProps) {
  const { account, user } = useMondayAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const isReconnect = variant === 'reconnect';

  const handleConnect = () => {
    if (!account?.id) return;

    setIsRedirecting(true);

    const params = new URLSearchParams({
      accountId: account.id.toString(),
      userId: (user?.id || 0).toString(),
      returnUrl: '/',
    });

    if (account.slug) {
      params.set('slug', account.slug);
    }

    window.location.href = `/api/auth/oauth/authorize?${params.toString()}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--surface-card)]">
      <div className="text-center p-8 max-w-[480px] w-full">
        {/* TaskLoops icon */}
        <div className="mb-6">
          <img src="/taskloops-logo.png" alt="TaskLoops" className="w-16 h-16 mx-auto rounded-2xl" />
        </div>

        {/* Heading */}
        <h1 className="text-xl font-bold font-heading text-[var(--text-heading)] mb-2">
          {isReconnect ? 'Reauthorize TaskLoops' : 'Authorize TaskLoops'}
        </h1>
        <p className="text-[var(--text-secondary)] text-sm mb-8">
          {isReconnect
            ? 'Your monday.com authorization has expired. Reauthorize to resume operations.'
            : 'TaskLoops needs your authorization to access your monday.com boards, import data, run operations, and create items on your behalf.'}
        </p>

        {/* Trust signals */}
        <div className="max-w-xs mx-auto mb-8 space-y-3 text-left">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-[var(--text-secondary)] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
            <span className="text-sm text-[var(--text-body)]">We only access boards you choose — never your full account</span>
          </div>
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-[var(--text-secondary)] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
            </svg>
            <span className="text-sm text-[var(--text-body)]">You can revoke access anytime from your monday.com account settings</span>
          </div>
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-[var(--text-secondary)] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
            <span className="text-sm text-[var(--text-body)]">All data is encrypted and never shared</span>
          </div>
        </div>

        {/* Connect button */}
        <button
          onClick={handleConnect}
          disabled={isRedirecting || !account?.id}
          className="px-8 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRedirecting ? (
            <span className="inline-flex items-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Redirecting to monday.com...
            </span>
          ) : (
            isReconnect ? 'Reauthorize TaskLoops' : 'Authorize TaskLoops'
          )}
        </button>

        {/* Helper text */}
        <p className="text-xs text-[var(--text-muted)] mt-4">
          You&apos;ll be briefly redirected to monday.com to authorize.
        </p>

        {/* Support link */}
        <p className="text-xs text-[var(--text-muted)] mt-6">
          Having trouble?{' '}
          <a href="mailto:hello@taskloops.com" className="text-primary-500 hover:text-primary-600">
            Contact hello@taskloops.com
          </a>
        </p>
      </div>
    </div>
  );
}
